import React, { useState, useEffect, useRef } from 'react';
import './Lightbox.css';
import { playsInDrivePlayer } from '../../lib/driveMedia';

const MOUNT_WINDOW = 2; // slides rendered on each side of the current one; others stay empty
const drivePreviewUrl = id => `https://drive.google.com/file/d/${id}/preview`;
const driveDownloadUrl = id => `https://drive.google.com/uc?export=download&id=${id}`;
const fileUrl = id =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port && window.location.port !== '80' ? ':' + window.location.port : ''}/imageproxy?id=${id}`;

// Phones that can hand files to the OS share sheet get "Save to Photos"; others download directly
const supportsSaveToPhotos = () => {
  try {
    return window.matchMedia('(pointer: coarse)').matches && !!navigator.canShare &&
      navigator.canShare({ files: [new File([''], 'probe.jpg', { type: 'image/jpeg' })] });
  } catch (error) {
    return false;
  }
};

const Lightbox = ({ 
  isOpen, 
  onClose, 
  currentImage, 
  images, 
  onNext, 
  onPrev, 
  currentIndex 
}) => {
  const [dragStart, setDragStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState(0);
  const [carouselOffset, setCarouselOffset] = useState(0);
  const thumbnailsRef = useRef(null);
  const carouselRef = useRef(null);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [preparedFile, setPreparedFile] = useState(null); // { id, file } ready for the share sheet
  const [saveError, setSaveError] = useState('');
  const [saveMenuPos, setSaveMenuPos] = useState(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handleTransition(-1);
    if (e.key === 'ArrowRight') handleTransition(1);
  };

  // Carousel transition handlers with momentum and pause
  const handleTransition = (direction) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setTransitionDirection(direction);
    
    // Calculate new offset
    const newOffset = carouselOffset - (direction * 100);
    setCarouselOffset(newOffset);
    
    // Add momentum delay before changing image - ลดเวลาให้ ultra smooth
    setTimeout(() => {
      if (direction === 1) {
        onNext();
      } else if (direction === -1) {
        onPrev();
      }
      setIsTransitioning(false);
      setTransitionDirection(0);
    }, 550); // ให้สอดคล้องกับ CSS transition 0.6s
  };

  // Update carousel offset when currentIndex changes
  useEffect(() => {
    setCarouselOffset(-currentIndex * 100);
  }, [currentIndex]);

  // Drag/Swipe handlers
  const handleMouseDown = (e) => {
    if (isTransitioning) return; // ป้องกันการเริ่ม drag ใหม่ขณะกำลัง transition
    setDragStart(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragStart || isTransitioning) return;
    const offset = e.clientX - dragStart;
    // จำกัดการเลื่อนให้ไม่เกิน 100px
    const limitedOffset = Math.max(-100, Math.min(100, offset));
    setDragOffset(limitedOffset);
  };

  const handleMouseUp = () => {
    if (!isDragging || isTransitioning) return;
    
    const threshold = 30; // ลด threshold ให้ swipe เล็กน้อยก็เลื่อนได้
    const velocity = Math.abs(dragOffset);
    
    // ตรวจสอบความเร็วของการ swipe
    if (velocity < threshold) {
      // ถ้า swipe ไม่แรงพอ ให้กลับไปที่ตำแหน่งเดิม
      setDragStart(null);
      setDragOffset(0);
      setIsDragging(false);
      return;
    }
    
    // Natural swipe: left to right = previous, right to left = next
    if (dragOffset > threshold) {
      handleTransition(-1); // Swipe left to right shows previous image
    } else if (dragOffset < -threshold) {
      handleTransition(1); // Swipe right to left shows next image
    }
    
    setDragStart(null);
    setDragOffset(0);
    setIsDragging(false);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (isTransitioning) return; // ป้องกันการเริ่ม drag ใหม่ขณะกำลัง transition
    setDragStart(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !dragStart || isTransitioning) return;
    const offset = e.touches[0].clientX - dragStart;
    // จำกัดการเลื่อนให้ไม่เกิน 100px
    const limitedOffset = Math.max(-100, Math.min(100, offset));
    setDragOffset(limitedOffset);
  };

  const handleTouchEnd = () => {
    if (!isDragging || isTransitioning) return;
    
    const threshold = 30; // ลด threshold ให้ swipe เล็กน้อยก็เลื่อนได้
    const velocity = Math.abs(dragOffset);
    
    // ตรวจสอบความเร็วของการ swipe
    if (velocity < threshold) {
      // ถ้า swipe ไม่แรงพอ ให้กลับไปที่ตำแหน่งเดิม
      setDragStart(null);
      setDragOffset(0);
      setIsDragging(false);
      return;
    }
    
    // Natural swipe: left to right = previous, right to left = next
    if (dragOffset > threshold) {
      handleTransition(-1); // Swipe left to right shows previous image
    } else if (dragOffset < -threshold) {
      handleTransition(1); // Swipe right to left shows next image
    }
    
    setDragStart(null);
    setDragOffset(0);
    setIsDragging(false);
  };

  const handleThumbnailClick = (index) => {
    // This will be handled by parent component
    const event = { target: { value: index } };
    // We need to pass the index back to parent
    if (window.lightboxThumbnailClick) {
      window.lightboxThumbnailClick(index);
    }
  };

  const downloadFile = image => {
    const link = document.createElement('a');
    link.href = fileUrl(image.id);
    link.download = image.name || `image-${currentIndex + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // iOS only opens the share sheet straight from a tap, so the file is fetched when the menu opens
  const prepareFile = image => {
    setPreparedFile(null);
    fetch(fileUrl(image.id))
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then(blob => setPreparedFile({
        id: image.id,
        file: new File([blob], image.name || 'photo.jpg', { type: blob.type || image.mimeType }),
      }))
      .catch(() => setSaveError('Could not prepare the file. Use Download instead.'));
  };

  const handleSave = event => {
    // Large videos cannot go through the proxy; let Drive handle the download
    if (playsInDrivePlayer(currentImage)) {
      window.open(driveDownloadUrl(currentImage.id), '_blank', 'noopener');
      return;
    }
    if (!supportsSaveToPhotos()) {
      downloadFile(currentImage);
      return;
    }
    // Anchor the menu just below the save button, whatever offset the container has
    const button = event.currentTarget;
    setSaveMenuPos({ top: button.offsetTop + button.offsetHeight + 8, left: button.offsetLeft });
    setSaveError('');
    setSaveMenuOpen(true);
    if (!preparedFile || preparedFile.id !== currentImage.id) prepareFile(currentImage);
  };

  const fileReady = !!preparedFile && preparedFile.id === currentImage.id;

  const saveToPhotos = () => {
    if (!fileReady) return;
    navigator.share({ files: [preparedFile.file] })
      .then(() => setSaveMenuOpen(false))
      .catch(error => {
        if (error.name !== 'AbortError') setSaveError('Saving was blocked. Use Download instead.');
      });
  };

  const renderSlide = (image, index) => {
    if (Math.abs(index - currentIndex) > MOUNT_WINDOW) return null;

    if (playsInDrivePlayer(image)) {
      // Only the active slide mounts the player so off-screen videos never load
      return index === currentIndex ? (
        <iframe
          src={drivePreviewUrl(image.id)}
          title={image.name}
          className="lightbox-video-frame"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      ) : (
        <img src={image.thumb} alt={image.name} className="lightbox-image" draggable={false} />
      );
    }

    if (/^video\//.test(image.mimeType || '')) {
      return <video src={image.file} className="lightbox-image" autoPlay muted loop playsInline />;
    }

    return <img src={image.mimeType === 'image/gif' ? image.file : image.full} alt={image.name} className="lightbox-image" draggable={false} />;
  };

  // Auto-scroll thumbnail to current image
  const scrollToCurrentThumbnail = () => {
    if (thumbnailsRef.current) {
      const thumbnailWidth = 68; // 60px + 8px gap
      const containerWidth = thumbnailsRef.current.clientWidth;
      const scrollPosition = (currentIndex * thumbnailWidth) - (containerWidth / 2) + (thumbnailWidth / 2);
      
      thumbnailsRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.overflow = 'unset';
    };
  }, [dragStart, dragOffset, isDragging]);

  // A save menu belongs to the photo it was opened on
  useEffect(() => {
    setSaveMenuOpen(false);
    setSaveError('');
  }, [currentIndex]);

  // Auto-scroll when currentIndex changes
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToCurrentThumbnail, 100); // Small delay to ensure DOM is ready
    }
  }, [currentIndex, isOpen]);

  return (
    <div className="lightbox-overlay" onClick={handleBackdropClick}>
      <div className="lightbox-container">
        <button className="lightbox-close" onClick={onClose}>
          <span>&times;</span>
          <span className="lightbox-close-text">close</span>
        </button>
        
        <button className="lightbox-download" onClick={handleSave} aria-haspopup="menu" aria-expanded={saveMenuOpen}>
          <span>&#10515;</span>
          <span className="lightbox-download-text">save</span>
        </button>

        {saveMenuOpen && (
          <React.Fragment>
            <div className="lightbox-save-backdrop" onClick={() => setSaveMenuOpen(false)} />
            <div className="lightbox-save-menu" role="menu" style={saveMenuPos}>
              <button type="button" role="menuitem" disabled={!fileReady} onClick={saveToPhotos}>
                {fileReady
                  ? (/^video\//.test(currentImage.mimeType || '') ? 'Save video to Photos' : 'Save to Photos')
                  : 'Preparing…'}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  downloadFile(currentImage);
                  setSaveMenuOpen(false);
                }}
              >
                Download file
              </button>
              {saveError && <p className="lightbox-save-error" role="alert">{saveError}</p>}
            </div>
          </React.Fragment>
        )}
        
        <button className="lightbox-nav lightbox-prev" onClick={() => handleTransition(-1)}>
          <span>&#8592;</span>
        </button>
        
        <div 
          className="lightbox-carousel-container"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            ref={carouselRef}
            className="lightbox-carousel"
            style={{
              transform: `translateX(${carouselOffset + dragOffset}%)`,
              transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
            }}
          >
            {images.map((image, index) => (
              <div key={index} className="lightbox-carousel-item">
                {renderSlide(image, index)}
              </div>
            ))}
          </div>
          
          {/* Swipe direction indicator */}
          {isDragging && Math.abs(dragOffset) > 15 && (
            <div 
              className="swipe-indicator"
              style={{
                left: dragOffset > 0 ? '20px' : 'auto',
                right: dragOffset < 0 ? '20px' : 'auto',
                opacity: Math.min(1, Math.abs(dragOffset) / 50)
              }}
            >
              {dragOffset > 0 ? '' : ''} 
            </div>
          )}
        </div>
        
        <button className="lightbox-nav lightbox-next" onClick={() => handleTransition(1)}>
          <span>&#8594;</span>
        </button>
        


        {/* Thumbnail Navigation */}
        <div className="lightbox-thumbnails" ref={thumbnailsRef}>
          <div className="lightbox-counter-overlay">
            <span className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </span>
          </div>
          {images.map((image, index) => (
            <div
              key={index}
              className={`lightbox-thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img 
                src={image.thumb} 
                alt={image.name}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
